import {
  Drawer,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Box,
  LinearProgress,
  Pagination,
  IconButton,
} from "@mui/material";
import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { displayDate } from "./action";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import useBatchJobPolling from "../hooks/useBatchPolling";
import OCRDisplayDrawer from "./OCRDisplayDrawer";
import LinearProgressWithLabel from "./LinearProgressWithLabel";

const minifyText = (str, length = 40) => {
  if (str.length > length) {
    return str.substring(0, length) + "...";
  } else {
    return str;
  }
};

// const BatchMarkingCard = ({ data, click }) => {
//   return (
//     <Card sx={{ height: "100%", cursor: "pointer" }} onClick={click}>
//       <CardContent>
//         <Typography variant="subtitle2" color="text.secondary" gutterBottom>
//           {displayDate(data._createdAt)}
//         </Typography>
//         <Typography variant="body1" component="div">
//           {data?.topic ? minifyText(data?.topic) : minifyText(data?.input)}
//         </Typography>
//         <Typography variant="subtitle2">評改作文</Typography>
//         <Typography variant="subtitle2">{data.marking_scheme}</Typography>
//       </CardContent>
//     </Card>
//   );
// };

const BatchMarkingAccordion = ({
  data,
  token,
  expanded,
  onChange,
  resultFn,
}) => {
  const theme = useTheme();
  const jobIds = data.batchMaiCallAiJobIds || [];
  const { jobDetails, progress } = useBatchJobPolling(jobIds, token);
  const clickHandler = (data) => {
    resultFn(data);
  };
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{
        mb: 1,
        position: "relative",
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.info.contrastText,
        }}
      >
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <Typography variant="subtitle2" gutterBottom>
            評改作文 ({data.marking_scheme}) ({displayDate(data.createdAt)})
          </Typography>
          <Typography variant="body1" component="div">
            {minifyText(data?.topic)}
          </Typography>
          <Box sx={{ width: "100%" }}>
            <LinearProgressWithLabel
              value={progress.percent}
              progress={progress}
            />
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {jobIds.length > 0 && (
          <Stack direction="column" spacing={2}>
            {jobDetails.map((job, idx) => (
              <Card key={job._id || idx} sx={{ mb: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" color="text.secondary">
                      作文輸入 {idx + 1}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        job?.status === "completed"
                          ? "已完成"
                          : job?.status === "processing"
                          ? "正在評改中…"
                          : job?.status === "error"
                          ? "錯誤"
                          : job?.status === "failed"
                          ? "失敗"
                          : "排列中"
                      }
                      color={
                        job?.status === "completed"
                          ? "success"
                          : job?.status === "processing"
                          ? "info"
                          : job?.status === "error" || job?.status === "failed"
                          ? "error"
                          : "default"
                      }
                    />
                  </Stack>
                  {(job?.status === "processing" ||
                    job?.status === "queued") && (
                    <LinearProgress sx={{ mt: 2, mb: 2 }} />
                  )}
                  {job?.data?.input && job?.data?.input?.length > 0 && (
                    <Typography variant="body1" component="div">
                      {minifyText(job.data?.input)}
                    </Typography>
                  )}
                  <div style={{ height: "10px" }} />
                  {job?.data?.ocrImages && job?.data?.ocrImages?.length > 0 && (
                    <OCRDisplayDrawer ocrImages={job.data.ocrImages} />
                  )}
                  <Box
                    sx={{
                      display: "flex",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={() => clickHandler(job)}
                      sx={{
                        mt: 1,
                        display: job?.status === "completed" ? "block" : "none",
                      }}
                    >
                      查看評改結果
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const BatchMarkingDrawer = ({
  batchHistoryData,
  state,
  setDrawer,
  resultFn,
  token,
}) => {
  const theme = useTheme();

  console.log("BatchMarkingDrawer", batchHistoryData);
  const [expandedIdx, setExpandedIdx] = React.useState(false);

  // Pagination state
  const [page, setPage] = React.useState(1);
  const itemsPerPage = 4;
  const totalPages = Math.ceil((batchHistoryData?.length || 0) / itemsPerPage);

  // Slice data for current page
  const paginatedData = batchHistoryData
    ? batchHistoryData.slice((page - 1) * itemsPerPage, page * itemsPerPage)
    : [];

  const handlePageChange = (event, value) => {
    setPage(value);
    setExpandedIdx(false); // Optionally collapse all accordions on page change
  };

  if (!batchHistoryData) {
    return null;
  }

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawer(open);
  };

  return (
    <Drawer anchor="bottom" open={state} onClose={toggleDrawer(false)}>
      <Grid
        container
        spacing={2}
        sx={{
          p: 3,
          background: theme.palette.primary.dark,
          color: theme.palette.info.contrastText,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <Typography variant="h5" sx={{ my: 3, mx: 5 }}>
            量批評改紀錄(測試版)
          </Typography>
          {/* <Grid item >
            <Button
              variant="contained"
              size="large"
              onClick={toggleDrawer(false)}
              sx={{
                width: "100%",
                background: theme.palette.common.white,
                color: theme.palette.common.white.contrastText,
              }}
            >
              ❌
            </Button>
          </Grid> */}
          <IconButton
            aria-label="close"
            onClick={toggleDrawer(false)}
            sx={{
              color: (theme) => theme.palette.grey[50],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box
          sx={{
            width: { xs: "100%", md: "90%" },
            alignContent: "center",
            margin: "0 auto",
          }}
        >
          {paginatedData.map((data, idx) => {
            const globalIdx = (page - 1) * itemsPerPage + idx;
            return (
              <Grid item xs={12} key={`history-${globalIdx}`}>
                <BatchMarkingAccordion
                  data={data}
                  token={token}
                  resultFn={resultFn}
                  expanded={expandedIdx === globalIdx}
                  onChange={() =>
                    setExpandedIdx(
                      expandedIdx === globalIdx ? false : globalIdx
                    )
                  }
                />
              </Grid>
            );
          })}
        </Box>
        {/* Pagination controls */}
        {totalPages > 1 && (
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 2,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Grid>
        )}
        {/* <Grid item xs={12} sx={{}}>
          <Button
            variant="contained"
            size="large"
            onClick={toggleDrawer(false)}
            startIcon={<CloseIcon />}
            sx={{
              width: "100%",
            }}
          >
            關閉
          </Button>
        </Grid> */}
      </Grid>
    </Drawer>
  );
};

export default BatchMarkingDrawer;
